import { useState, useRef, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  PlusIcon,
  MinusIcon,
  TrashIcon,
  QrCodeIcon,
  ShoppingCartIcon,
  UserIcon,
  BanknotesIcon,
  CreditCardIcon,
  DevicePhoneMobileIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline'
import { saleService } from '../../services/saleService'
import { customerService } from '../../services/customerService'
import { productService } from '../../services/productService'
import { settingService } from '../../services/settingService'
import { ReceiptModal } from '../../components/sales/ReceiptModal'
import toast from 'react-hot-toast'

interface CartItem {
  id: string
  product_id: number
  name: string
  sku: string
  price: number
  quantity: number
  serials?: number[]
  is_bundle?: boolean
  bundle_id?: number
}

interface CustomerSale {
  sale_id: number
  sale_number: string
  sale_date: string
  total_amount: number
  paid_amount: number
  balance_due: number
  status: string
  item_count: number
  items: {
    product_name: string
    quantity: number
    unit_price: number
  }[]
}

export const POSPage = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const location = useLocation()
  const [cart, setCart] = useState<CartItem[]>([])
  const [barcode, setBarcode] = useState('')
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null)
  const [showCustomerModal, setShowCustomerModal] = useState(false)
  const [showCustomerHistory, setShowCustomerHistory] = useState(false)
  const [discountAmount, setDiscountAmount] = useState(0)
  const [discountPercent, setDiscountPercent] = useState(0)
  const [vatType, setVatType] = useState<'zero_vat' | 'vat_exclusive' | 'vat_inclusive'>('zero_vat')
  const [taxPercent, setTaxPercent] = useState(16)
  const [termsConditions, setTermsConditions] = useState('')
  const [defaultSaleTerms, setDefaultSaleTerms] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<any[]>([])
  const [showSearchResults, setShowSearchResults] = useState(false)
  const barcodeInputRef = useRef<HTMLInputElement>(null)
  const searchContainerRef = useRef<HTMLDivElement>(null)

  const { data: customersData } = useQuery({
    queryKey: ['customers', 'pos'],
    queryFn: () => customerService.getAll({ limit: 100 }),
  })

  const { data: settingsData, isFetching: isSettingsFetching } = useQuery({
    queryKey: ['settings-documents'],
    queryFn: () => settingService.getAllSettings(),
    staleTime: 0,
  })
  const [termsInitialized, setTermsInitialized] = useState(false)
  useEffect(() => {
    if (termsInitialized || isSettingsFetching || !settingsData) return
    const rows: any[] = settingsData?.data?.data || []
    const def = rows.find((r: any) => r.setting_key === 'default_sale_terms')?.setting_value || ''
    setTermsConditions(def)
    setDefaultSaleTerms(def)
    setTermsInitialized(true)
  }, [settingsData, isSettingsFetching])

  const { data: customerHistoryData, refetch: refetchCustomerHistory } = useQuery({
    queryKey: ['customer-pos-history', selectedCustomer?.customer_id],
    queryFn: () => customerService.getPOSHistory(selectedCustomer!.customer_id),
    enabled: !!selectedCustomer,
  })

  const scanMutation = useMutation({
    mutationFn: (code: string) => saleService.scanBarcode(code),
    onSuccess: (response) => {
      const data = response.data.data
      if (data.type === 'product' || data.type === 'serial') {
        const item = data.item || data.product
        addToCart({
          product_id: item.product_id,
          name: item.product_name,
          sku: item.sku,
          price: Number(item.price),
          serials: data.type === 'serial' ? [item.serial_id] : undefined,
        })
      } else if (data.type === 'bundle_instance') {
        const bundle = data.bundle
        const instance = data.instance
        // Extract all serial IDs from the bundle instance components
        const serialIds = instance?.components?.map((c: any) => c.serial?.serial_id).filter(Boolean) || []
        addToCart({
          product_id: bundle.bundle_id,
          name: `${bundle.bundle_name} (Bundle)`,
          sku: bundle.bundle_sku,
          price: Number(bundle.bundle_components?.[0]?.products?.price_history?.[0]?.selling_price || 0),
          is_bundle: true,
          bundle_id: bundle.bundle_id,
          serials: serialIds,
        })
        toast.success(`Added bundle with ${serialIds.length} items`)
      } else if (data.type === 'bundle') {
        const bundle = data.item || data.bundle
        addToCart({
          product_id: bundle.bundle_id,
          name: bundle.bundle_name,
          sku: bundle.bundle_sku,
          price: Number(bundle.unit_price || 0),
          is_bundle: true,
          bundle_id: bundle.bundle_id,
        })
      }
      setBarcode('')
      barcodeInputRef.current?.focus()
    },
    onError: () => {
      toast.error('Item not found')
      setBarcode('')
    },
  })

  const [createdSale, setCreatedSale] = useState<any>(null)
  const [showReceipt, setShowReceipt] = useState(false)

  const createSaleMutation = useMutation({
    mutationFn: (data: any) => saleService.create(data),
    onSuccess: (response) => {
      toast.success('Sale created successfully')
      const sale = response.data.data
      // Navigate to sale detail with payment modal open
      navigate(`/sales/${sale.sale_id}`, { state: { openPaymentModal: true } })
      // Clear cart after successful sale
      setCart([])
      setDiscountAmount(0)
      setDiscountPercent(0)
      setVatType('zero_vat')
      setTaxPercent(16)
      setTermsConditions(defaultSaleTerms)
      setSelectedCustomer(null)
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || 'Failed to create sale'
      toast.error(message)
    },
  })

  const handlePaymentComplete = async () => {
    // Refresh sale data after payment
    if (createdSale) {
      try {
        const response = await saleService.getById(createdSale.sale_id)
        setCreatedSale(response.data.data)
      } catch {
        // If refresh fails, just close the receipt
        setShowReceipt(false)
        navigate(`/sales/${createdSale.sale_id}`)
      }
    }
  }

  const addToCart = (item: Partial<CartItem>) => {
    setCart(prev => {
      const existing = prev.find(i => i.product_id === item.product_id && !item.is_bundle)
      if (existing && !item.serials) {
        return prev.map(i => 
          i.id === existing.id 
            ? { ...i, quantity: i.quantity + 1 }
            : i
        )
      }
      return [...prev, { ...item, id: Date.now().toString(), quantity: 1 } as CartItem]
    })
  }

  const updateQuantity = (id: string, delta: number) => {
    setCart(prev => prev.map(item => {
      if (item.id === id) {
        const newQty = Math.max(1, item.quantity + delta)
        return { ...item, quantity: newQty }
      }
      return item
    }))
  }

  const removeItem = (id: string) => {
    setCart(prev => prev.filter(item => item.id !== id))
  }

  const updateItemPrice = (id: string, newPrice: number) => {
    setCart(prev => prev.map(item =>
      item.id === id ? { ...item, price: Math.max(0, newPrice) } : item
    ))
  }

  const handleBarcodeSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (barcode.trim()) {
      scanMutation.mutate(barcode.trim())
    }
  }

  // Product search by name
  const handleSearch = async (query: string) => {
    setSearchQuery(query)
    if (query.trim().length < 2) {
      setSearchResults([])
      setShowSearchResults(false)
      return
    }
    
    try {
      const response = await productService.search(query)
      setSearchResults(response.data?.data || [])
      setShowSearchResults(true)
    } catch {
      setSearchResults([])
      setShowSearchResults(false)
    }
  }

  const handleSelectProduct = (product: any) => {
    // Get price from price history or default - handle Decimal types from Prisma
    const priceHistory = product.price_history || []
    const latestPrice = priceHistory[0]
    // Convert to number, handling both string and number types
    const price = latestPrice?.selling_price 
      ? Number(latestPrice.selling_price) 
      : 0
    
    addToCart({
      product_id: product.product_id,
      name: product.product_name,
      sku: product.sku,
      price: price,
    })
    
    // Show success message and clear search
    toast.success(`Added ${product.product_name} to cart`)
    
    setSearchQuery('')
    setSearchResults([])
    setShowSearchResults(false)
    
    // Focus back on barcode input for quick scanning
    setTimeout(() => {
      barcodeInputRef.current?.focus()
    }, 100)
  }

  const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0)
  const discount = discountPercent > 0 
    ? subtotal * (discountPercent / 100) 
    : discountAmount
  const total = subtotal - discount

  const handleCheckout = () => {
    if (cart.length === 0) {
      toast.error('Cart is empty')
      return
    }

    // Validate each item has a valid price
    for (const item of cart) {
      if (!item.price || item.price <= 0) {
        toast.error(`Item "${item.name}" has an invalid price`)
        return
      }
      if (!item.quantity || item.quantity <= 0) {
        toast.error(`Item "${item.name}" has an invalid quantity`)
        return
      }
    }

    // Validate total amount
    if (total <= 0) {
      toast.error('Total amount must be greater than 0')
      return
    }

    createSaleMutation.mutate({
      customer_id: selectedCustomer?.customer_id,
      sale_type: selectedCustomer?.customer_groups?.price_tier || 'retail',
      items: cart.map(item => ({
        product_id: item.product_id,
        bundle_id: item.bundle_id,
        quantity: item.quantity,
        unit_price: item.price,
        is_bundle: item.is_bundle,
        serials: item.serials || [],
      })),
      discount_amount: discount,
      discount_percent: discountPercent,
      vat_type: vatType,
      tax_percent: taxPercent,
      terms_conditions: termsConditions || null,
    })
  }

  const handleSelectCustomer = (customer: any) => {
    setSelectedCustomer(customer)
    setShowCustomerModal(false)
    if (customer) {
      setShowCustomerHistory(true)
      refetchCustomerHistory()
    } else {
      setShowCustomerHistory(false)
    }
  }

  const [customerSearch, setCustomerSearch] = useState('')
  
  const customers = customersData?.data?.data?.items || []
  const filteredCustomers = customerSearch
    ? customers.filter((c: any) => {
        const search = customerSearch.toLowerCase()
        const name = c.company_name || `${c.first_name} ${c.last_name}`
        return (
          name.toLowerCase().includes(search) ||
          c.phone?.toLowerCase().includes(search) ||
          c.email?.toLowerCase().includes(search)
        )
      })
    : customers
  const customerHistory = customerHistoryData?.data?.data
  const recentSales: CustomerSale[] = customerHistory?.recent_sales || []
  const outstandingBalance = customerHistory?.summary?.outstanding_balance || 0
  const creditAvailable = customerHistory?.summary?.credit_available
  const totalPurchases = customerHistory?.summary?.total_purchases || 0

  // Handle quotation conversion data
  useEffect(() => {
    const quotationData = location.state?.quotationData || JSON.parse(sessionStorage.getItem('quotationToConvert') || 'null')
    if (quotationData) {
      // Load customer if exists
      if (quotationData.customer) {
        setSelectedCustomer(quotationData.customer)
      }
      // Load items into cart
      const cartItems = quotationData.items.map((item: any, index: number) => ({
        id: `quote-item-${index}`,
        product_id: item.product_id,
        bundle_id: item.bundle_id,
        name: item.description || 'Unknown Item',
        sku: '',
        price: item.unit_price,
        quantity: item.quantity,
        discount: item.discount_amount || 0,
      }))
      setCart(cartItems)
      // Load discount
      if (quotationData.discount_percent) {
        setDiscountPercent(quotationData.discount_percent)
      } else if (quotationData.discount_amount) {
        setDiscountAmount(quotationData.discount_amount)
      }
      // Clear session storage
      sessionStorage.removeItem('quotationToConvert')
      // Clear location state
      window.history.replaceState({}, '')
      toast.success('Quotation loaded successfully')
    }
  }, [location.state])

  // Focus barcode input on mount
  useEffect(() => {
    barcodeInputRef.current?.focus()
  }, [])

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setShowSearchResults(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid': return <CheckCircleIcon className="w-4 h-4 text-green-500" />
      case 'partially_paid': return <ExclamationTriangleIcon className="w-4 h-4 text-yellow-500" />
      default: return <ClockIcon className="w-4 h-4 text-gray-400" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800'
      case 'partially_paid': return 'bg-yellow-100 text-yellow-800'
      case 'confirmed': return 'bg-blue-100 text-blue-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="h-[calc(100vh-6rem)] flex flex-col lg:flex-row gap-6">
      {/* Left Side - Product Entry */}
      <div className="flex-1 flex flex-col space-y-4">
        {/* Barcode Scanner */}
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <form onSubmit={handleBarcodeSubmit} className="flex gap-2">
            <div className="flex-1 relative">
              <QrCodeIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                ref={barcodeInputRef}
                type="text"
                placeholder="Scan barcode or enter SKU..."
                value={barcode}
                onChange={(e) => setBarcode(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-lg"
              />
            </div>
            <button
              type="submit"
              disabled={scanMutation.isPending || !barcode.trim()}
              className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {scanMutation.isPending ? '...' : 'Add'}
            </button>
          </form>
        </div>

        {/* Product Search */}
        <div ref={searchContainerRef} className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <div className="relative">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by product name..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  onFocus={() => searchQuery.trim().length >= 2 && setShowSearchResults(true)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-lg"
                />
              </div>
              {searchQuery && (
                <button
                  onClick={() => {
                    setSearchQuery('')
                    setSearchResults([])
                    setShowSearchResults(false)
                  }}
                  className="px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Clear
                </button>
              )}
            </div>
            
            {/* Search Results Dropdown */}
            {showSearchResults && searchResults.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-80 overflow-auto">
                <div className="p-2 text-xs text-gray-500 border-b">
                  {searchResults.length} product(s) found
                </div>
                {searchResults.map((product) => (
                  <button
                    type="button"
                    key={product.product_id}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      handleSelectProduct(product)
                    }}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0 flex items-center gap-3 cursor-pointer"
                  >
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      {product.product_images?.[0] ? (
                        <img
                          src={product.product_images[0].image_url}
                          alt={product.product_name}
                          className="w-full h-full object-cover rounded-lg"
                        />
                      ) : (
                        <QrCodeIcon className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{product.product_name}</p>
                      <p className="text-sm text-gray-500">SKU: {product.sku}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-primary-600">
                        KSh {Number(product.price_history?.[0]?.selling_price || 0).toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500">
                        {product._count?.serial_numbers ?? 0} in stock
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
            
            {showSearchResults && searchQuery.trim().length >= 2 && searchResults.length === 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 p-4 text-center text-gray-500">
                No products found matching "{searchQuery}"
              </div>
            )}
          </div>
        </div>

        {/* Cart Items */}
        <div className="flex-1 bg-white rounded-xl shadow border border-gray-100 overflow-hidden flex flex-col">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900 flex items-center gap-2">
              <ShoppingCartIcon className="w-5 h-5" />
              Cart ({cart.length} items)
            </h2>
            {cart.length > 0 && (
              <button
                onClick={() => setCart([])}
                className="text-sm text-red-600 hover:text-red-700"
              >
                Clear All
              </button>
            )}
          </div>

          <div className="flex-1 overflow-auto p-4">
            {cart.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-gray-400">
                <ShoppingCartIcon className="w-16 h-16 mb-4" />
                <p>Scan barcode or search to add items</p>
              </div>
            ) : (
              <div className="space-y-3">
                {cart.map((item) => (
                  <div key={item.id} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{item.name}</p>
                      <p className="text-sm text-gray-500">{item.sku}</p>
                      {item.serials && item.serials.length > 0 && (
                        <p className="text-xs text-gray-400">Serial tracked</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => updateQuantity(item.id, -1)}
                        className="p-1 hover:bg-gray-200 rounded"
                      >
                        <MinusIcon className="w-4 h-4" />
                      </button>
                      <span className="w-8 text-center font-medium">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.id, 1)}
                        className="p-1 hover:bg-gray-200 rounded"
                      >
                        <PlusIcon className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="text-right min-w-[120px]">
                      <p className="font-medium">KSh {(item.price * item.quantity).toLocaleString()}</p>
                      <div className="flex items-center justify-end gap-1">
                        <span className="text-xs text-gray-500">@</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.price}
                          onChange={(e) => updateItemPrice(item.id, parseFloat(e.target.value) || 0)}
                          className="w-20 text-right text-sm border border-gray-300 rounded px-1 py-0.5 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    </div>
                    <button
                      onClick={() => removeItem(item.id)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Customer History Section */}
        {selectedCustomer && showCustomerHistory && (
          <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                  <ClockIcon className="w-5 h-5" />
                  Customer History: {selectedCustomer.company_name || `${selectedCustomer.first_name} ${selectedCustomer.last_name}`}
                </h2>
                <button
                  onClick={() => setShowCustomerHistory(false)}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Hide
                </button>
              </div>
            </div>
            <div className="p-4">
              {/* Customer Summary Cards */}
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-sm text-blue-600">Total Purchases</p>
                  <p className="text-xl font-bold text-blue-900">KSh {totalPurchases.toLocaleString()}</p>
                </div>
                <div className={`rounded-lg p-3 ${outstandingBalance > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
                  <p className={`text-sm ${outstandingBalance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    Outstanding Balance
                  </p>
                  <p className={`text-xl font-bold ${outstandingBalance > 0 ? 'text-red-900' : 'text-green-900'}`}>
                    KSh {outstandingBalance.toLocaleString()}
                  </p>
                </div>
                {creditAvailable !== null && creditAvailable !== undefined && (
                  <div className="bg-purple-50 rounded-lg p-3">
                    <p className="text-sm text-purple-600">Credit Available</p>
                    <p className="text-xl font-bold text-purple-900">KSh {creditAvailable.toLocaleString()}</p>
                  </div>
                )}
              </div>

              {/* Recent Sales Table */}
              {recentSales.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No previous sales found</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Date</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Sale #</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Items</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Total</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Balance</th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {recentSales.map((sale) => (
                        <tr key={sale.sale_id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-sm text-gray-600">
                            {new Date(sale.sale_date).toLocaleDateString()}
                          </td>
                          <td className="px-3 py-2">
                            <button
                              onClick={() => navigate(`/sales/${sale.sale_id}`)}
                              className="text-sm text-primary-600 hover:underline"
                            >
                              {sale.sale_number}
                            </button>
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-600">
                            {sale.items.slice(0, 2).map((item, i) => (
                              <div key={i} className="truncate max-w-[150px]">
                                {item.quantity}x {item.product_name}
                              </div>
                            ))}
                            {sale.items.length > 2 && (
                              <div className="text-xs text-gray-400">+{sale.items.length - 2} more</div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right text-sm font-medium">
                            KSh {sale.total_amount.toLocaleString()}
                          </td>
                          <td className="px-3 py-2 text-right text-sm">
                            {sale.balance_due > 0 ? (
                              <span className="text-red-600">KSh {sale.balance_due.toLocaleString()}</span>
                            ) : (
                              <span className="text-green-600">Paid</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(sale.status)}`}>
                              {getStatusIcon(sale.status)}
                              {sale.status.replace('_', ' ')}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Right Side - Checkout */}
      <div className="w-full lg:w-96 bg-white rounded-xl shadow border border-gray-100 p-6 flex flex-col">
        <h2 className="font-semibold text-gray-900 mb-4">Checkout</h2>

        {/* Customer Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Customer</label>
          <button
            onClick={() => setShowCustomerModal(true)}
            className="w-full flex items-center gap-3 p-3 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
              <UserIcon className="w-5 h-5 text-gray-500" />
            </div>
            <div className="flex-1 text-left">
              {selectedCustomer ? (
                <>
                  <p className="font-medium text-gray-900">
                    {selectedCustomer.company_name || `${selectedCustomer.first_name} ${selectedCustomer.last_name}`}
                  </p>
                  <p className="text-sm text-gray-500">{selectedCustomer.customer_groups?.group_name || 'Retail'}</p>
                </>
              ) : (
                <p className="text-gray-500">Select customer (optional)</p>
              )}
            </div>
          </button>
          
          {/* Quick Balance Indicator */}
          {selectedCustomer && outstandingBalance > 0 && (
            <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2 text-red-700">
                <ExclamationTriangleIcon className="w-5 h-5" />
                <span className="font-medium">Outstanding Balance</span>
              </div>
              <p className="text-2xl font-bold text-red-900 mt-1">
                KSh {outstandingBalance.toLocaleString()}
              </p>
              <button
                onClick={() => setShowCustomerHistory(true)}
                className="text-sm text-red-600 hover:underline mt-1"
              >
                View history
              </button>
            </div>
          )}
          
          {selectedCustomer && outstandingBalance === 0 && (
            <div className="mt-2 p-2 bg-green-50 rounded-lg text-center">
              <span className="text-sm text-green-700 flex items-center justify-center gap-1">
                <CheckCircleIcon className="w-4 h-4" />
                No outstanding balance
              </span>
            </div>
          )}
        </div>

        {/* Discount */}
        <div className="mb-4 space-y-2">
          <label className="block text-sm font-medium text-gray-700">Discount</label>
          <div className="flex gap-2">
            <input
              type="number"
              placeholder="Amount"
              value={discountAmount || ''}
              onChange={(e) => {
                setDiscountAmount(Number(e.target.value))
                setDiscountPercent(0)
              }}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
            />
            <input
              type="number"
              placeholder="%"
              value={discountPercent || ''}
              onChange={(e) => {
                setDiscountPercent(Number(e.target.value))
                setDiscountAmount(0)
              }}
              className="w-20 px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
        </div>

        {/* VAT */}
        <div className="mb-4 space-y-2">
          <label className="block text-sm font-medium text-gray-700">VAT Type</label>
          <div className="flex flex-col gap-1">
            {(['zero_vat', 'vat_exclusive', 'vat_inclusive'] as const).map((type) => (
              <label key={type} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="pos_vat_type"
                  value={type}
                  checked={vatType === type}
                  onChange={() => setVatType(type)}
                  className="text-primary-600"
                />
                <span className="text-sm">
                  {type === 'zero_vat' ? 'Zero VAT' : type === 'vat_exclusive' ? 'VAT Exclusive' : 'VAT Inclusive'}
                </span>
              </label>
            ))}
          </div>
          {vatType !== 'zero_vat' && (
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Rate:</label>
              <input
                type="number"
                value={taxPercent}
                onChange={(e) => setTaxPercent(Number(e.target.value))}
                className="w-20 border rounded px-2 py-1 text-sm"
                min={0}
                max={100}
              />
              <span className="text-sm text-gray-500">%</span>
            </div>
          )}
        </div>

        {/* Terms & Conditions */}
        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-600 mb-1">Terms & Conditions</label>
          <textarea
            value={termsConditions}
            onChange={(e) => setTermsConditions(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            placeholder="Terms and conditions..."
          />
          {termsConditions && termsConditions !== defaultSaleTerms && (
            <p className="mt-1 text-xs text-amber-600">
              Differs from default.{' '}
              <button
                type="button"
                className="underline font-medium hover:text-amber-800"
                onClick={() =>
                  settingService.updateSetting('default_sale_terms', { value: termsConditions })
                    .then(() => { setDefaultSaleTerms(termsConditions); queryClient.invalidateQueries({ queryKey: ['settings-documents'] }); toast.success('Default sale T&C updated'); })
                    .catch(() => toast.error('Failed to update default'))
                }
              >
                Save as new default?
              </button>
            </p>
          )}
        </div>

        {/* Summary */}
        <div className="flex-1 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Subtotal</span>
            <span>KSh {subtotal.toLocaleString()}</span>
          </div>
          {discount > 0 && (
            <div className="flex justify-between text-sm text-green-600">
              <span>Discount</span>
              <span>- KSh {discount.toLocaleString()}</span>
            </div>
          )}
          <div className="border-t pt-2 mt-2">
            <div className="flex justify-between text-lg font-bold">
              <span>Total</span>
              <span>KSh {total.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Payment Buttons */}
        <div className="mt-6 space-y-2">
          <button
            onClick={handleCheckout}
            disabled={cart.length === 0 || createSaleMutation.isPending}
            className="w-full py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 font-medium"
          >
            {createSaleMutation.isPending ? 'Processing...' : 'Complete Sale'}
          </button>
          <div className="grid grid-cols-3 gap-2">
            <button className="py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex flex-col items-center gap-1">
              <BanknotesIcon className="w-5 h-5" />
              <span className="text-xs">Cash</span>
            </button>
            <button className="py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex flex-col items-center gap-1">
              <DevicePhoneMobileIcon className="w-5 h-5" />
              <span className="text-xs">M-Pesa</span>
            </button>
            <button className="py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex flex-col items-center gap-1">
              <CreditCardIcon className="w-5 h-5" />
              <span className="text-xs">Card</span>
            </button>
          </div>
        </div>
      </div>

      {/* Customer Selection Modal */}
      {showCustomerModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[80vh] flex flex-col">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold">Select Customer</h3>
              <button 
                onClick={() => {
                  setShowCustomerModal(false)
                  setCustomerSearch('')
                }}
              >
                ✕
              </button>
            </div>
            <div className="p-4 border-b">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, phone, or email..."
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  autoFocus
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {filteredCustomers.length} of {customers.length} customers
              </p>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <div className="space-y-2">
                <button
                  onClick={() => handleSelectCustomer(null)}
                  className="w-full p-3 text-left border rounded-lg hover:bg-gray-50"
                >
                  <p className="font-medium">Walk-in Customer</p>
                  <p className="text-sm text-gray-500">Retail sale</p>
                </button>
                {filteredCustomers.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>No customers found</p>
                    <p className="text-sm">Try a different search term</p>
                  </div>
                ) : (
                  filteredCustomers.map((customer: any) => (
                    <button
                      key={customer.customer_id}
                      onClick={() => handleSelectCustomer(customer)}
                      className="w-full p-3 text-left border rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">
                            {customer.company_name || `${customer.first_name} ${customer.last_name}`}
                          </p>
                          <div className="text-sm text-gray-500 space-y-0.5">
                            <p>{customer.customer_groups?.group_name || 'Retail'}</p>
                            {customer.phone && <p className="text-xs">{customer.phone}</p>}
                          </div>
                        </div>
                        {customer.current_balance > 0 && (
                          <span className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded shrink-0">
                            KSh {Number(customer.current_balance).toLocaleString()}
                          </span>
                        )}
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Modal */}
      <ReceiptModal
        isOpen={showReceipt}
        onClose={() => {
          setShowReceipt(false)
          // Navigate to sale detail after closing receipt
          if (createdSale) {
            navigate(`/sales/${createdSale.sale_id}`)
          }
        }}
        sale={createdSale}
        onPaymentComplete={handlePaymentComplete}
      />
    </div>
  )
}
